<script setup lang="ts">
// DataTablePagination.vue
import type { Table } from '@tanstack/vue-table'
import type { Campaign } from './schema'

// Mantendo os componentes shadcn que você baixou
import { Button } from 'dashboard/components-shadcn/new-york/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'dashboard/components-shadcn/new-york/ui/select'
import { computed } from 'vue'

interface DataTablePaginationProps {
  table: Table<Campaign>
  tableStats?: {
    totalRows: number
    totalDataRows: number
    totalFilteredRows: number
    groupedRows: number
    expandedRows: number
    selectedRows: number
    allGroupsExpanded: boolean
    hasGrouping: boolean
  }
  isProcessing?: boolean
}

const props = withDefaults(defineProps<DataTablePaginationProps>(), {
  isProcessing: false,
})

// Computed para verificar se há agrupamento ativo
const hasGrouping = computed(() => {
  return props.tableStats?.hasGrouping || props.table.getState().grouping?.length > 0
})

// Função para atualizar tamanho da página
const updatePageSize = (value: string) => {
  const pageSize = parseInt(value)
  props.table.setPageSize(pageSize)
  // Resetar para primeira página quando alterar tamanho
  props.table.setPageIndex(0)
}
</script>

<template>
  <div class="pagination-wrapper">
    <!-- Controles de paginação -->
    <div class="flex items-center justify-between px-2 pagination-container">
      <!-- Informação de seleção -->
      <div class="flex-1 text-sm text-n-slate-10">
        <!-- Espaço reservado para informações adicionais se necessário -->
      </div>

      <!-- Controles de paginação - sempre visíveis -->
      <div class="flex items-center space-x-6 lg:space-x-8 pagination-controls">
        <!-- Seletor de tamanho da página -->
        <div class="flex items-center space-x-2">
          <p class="mt-2 text-sm font-medium text-n-slate-12">
            Linhas por página
          </p>
          <Select
            :model-value="`${table.getState().pagination.pageSize}`"
            @update:model-value="updatePageSize"
          >
            <SelectTrigger class="h-8 w-[70px] bg-n-slate-4 border-n-slate-6 text-n-slate-12">
              <SelectValue :placeholder="`${table.getState().pagination.pageSize}`" />
            </SelectTrigger>
            <SelectContent side="top" class="bg-n-slate-1 border-n-slate-6">
              <SelectItem v-for="pageSize in [10, 20, 30, 40, 50]" :key="pageSize" :value="`${pageSize}`">
                {{ pageSize }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Informação da página atual -->
        <div class="flex w-[100px] items-center justify-center text-sm font-medium text-n-slate-12 page-info">
          Página {{ table.getState().pagination.pageIndex + 1 }} de
          {{ table.getPageCount() }}
        </div>

        <!-- Botões de navegação -->
        <div class="flex items-center space-x-2">
          <!-- Primeira página -->
          <Button
            variant="outline"
            class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!table.getCanPreviousPage()"
            @click="table.setPageIndex(0)"
            title="Primeira página"
          >
            <span class="sr-only">Ir para primeira página</span>
            <span class="i-lucide-chevrons-left h-4 w-4 text-n-slate-11"></span>
          </Button>

          <!-- Página anterior -->
          <Button
            variant="outline"
            class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!table.getCanPreviousPage()"
            @click="table.previousPage()"
            title="Página anterior"
          >
            <span class="sr-only">Ir para página anterior</span>
            <span class="i-lucide-chevron-left h-4 w-4 text-n-slate-11"></span>
          </Button>

          <!-- Próxima página -->
          <Button
            variant="outline"
            class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!table.getCanNextPage()"
            @click="table.nextPage()"
            title="Próxima página"
          >
            <span class="sr-only">Ir para próxima página</span>
            <span class="i-lucide-chevron-right h-4 w-4 text-n-slate-11"></span>
          </Button>

          <!-- Última página -->
          <Button
            variant="outline"
            class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!table.getCanNextPage()"
            @click="table.setPageIndex(table.getPageCount() - 1)"
            title="Última página"
          >
            <span class="sr-only">Ir para última página</span>
            <span class="i-lucide-chevrons-right h-4 w-4 text-n-slate-11"></span>
          </Button>
        </div>
      </div>
    </div>

    <!-- Barra de status melhorada -->
    <div
      v-if="tableStats && tableStats.totalRows > 0"
      class="flex items-center justify-between text-xs text-n-slate-10 px-4 py-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-800 mt-4"
    >
      <div class="flex items-center gap-4">
        <!-- Estatísticas básicas -->
        <div class="flex items-center gap-1">
          <span class="i-lucide-database w-3 h-3"></span>
          <span v-if="!hasGrouping">
            {{ tableStats.totalFilteredRows }} linha(s)
          </span>
          <span v-else>
            {{ tableStats.totalDataRows }} registro(s) em {{ tableStats.groupedRows }} grupo(s)
          </span>
        </div>

        <!-- Grupos expandidos -->
        <div v-if="tableStats.groupedRows > 0" class="flex items-center gap-1">
          <span class="i-lucide-folder-open w-3 h-3"></span>
          <span>({{ tableStats.expandedRows }} grupo(s) expandido(s))</span>
        </div>

        <!-- Selecionados -->
        <div v-if="tableStats.selectedRows > 0" class="flex items-center gap-1 text-green-600 dark:text-green-400">
          <span class="i-lucide-check-square w-3 h-3"></span>
          <span>{{ tableStats.selectedRows }} selecionada(s)</span>
        </div>

        <!-- Indicador de agrupamento -->
        <div v-if="hasGrouping" class="flex items-center gap-1 text-green-600 dark:text-green-400">
          <span class="i-lucide-group w-3 h-3"></span>
          <span>Visualização agrupada</span>
        </div>
      </div>

      <!-- Indicador de processamento -->
      <div v-if="isProcessing" class="flex items-center gap-2">
        <div class="w-3 h-3 border border-green-500 border-t-transparent rounded-full animate-spin"></div>
        <span class="text-green-600 dark:text-green-400">Processando...</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Container de paginação seguindo o padrão WhatsApp */
.pagination-wrapper {
  @apply border border-slate-200  dark:border-slate-800 p-2  mb-2  rounded-md;

}

/* Pagination Styling seguindo o padrão */
:deep(.pagination-container) {
  @apply text-n-slate-10;
}

/* Garantir que as variáveis CSS do sistema funcionem no select */
:deep([data-radix-select-trigger]) {
  @apply bg-n-slate-1 border-n-slate-6 text-n-slate-12;
}

:deep([data-radix-select-trigger]:focus) {
  @apply ring-2 ring-green-500 border-green-500;
}

:deep([data-radix-select-content]) {
  @apply bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg p-1;
}

:deep([data-radix-select-item]) {
  @apply text-n-slate-12 bg-n-slate-1 cursor-pointer rounded px-3 py-2 mx-0.5;
}

:deep([data-radix-select-item]:hover) {
  @apply bg-n-slate-3;
}

:deep([data-radix-select-item][data-highlighted]) {
  @apply bg-n-slate-3 text-n-slate-12;
}

/* Estados disabled */
:deep(button:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Hover effects para botões seguindo o padrão WhatsApp */
:deep(button:not(:disabled):hover) {
  transition: all 0.2s ease;
}

/* Botões de paginação com cores originais */
:deep(.pagination-controls button) {
  @apply border-n-slate-6 hover:bg-n-slate-3;
}

/* Responsividade */
@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .page-info {
    width: auto;
    min-width: 120px;
  }
}

/* Animações suaves */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>